import { useState } from 'react';
import { Inbox } from 'lucide-react';
import { useSuspenseQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router';

import { EmptyBlock } from '@/components/ui/empty-block';
import Paginator from '@/components/ui/paginator';
import { AppRequestsDocument } from 'src/graphql/actions/appRequests/appRequests.query';
import { withSuspense } from 'src/hoc';
import { LinkApplicationDialog } from 'src/widgets/LinkApplicationDialog';

import { useVendorRequests, KIND_BY_FILTER, PER_PAGE } from '../model';

import { RequestCard } from './RequestCard';
import { DeclineRequestDialog } from './DeclineRequestDialog';
import { VendorRequestsListSkeleton } from './VendorRequestsListSkeleton';

const VendorRequestsListComponent = () => {
  const navigate = useNavigate();
  const { filter, page, setPage, declineRequest, linkRequest } = useVendorRequests();
  const [declineRequestId, setDeclineRequestId] = useState<string | null>(null);
  const [linkRequestId, setLinkRequestId] = useState<string | null>(null);

  const {
    data: { appRequests },
  } = useSuspenseQuery(AppRequestsDocument, {
    variables: { kind: KIND_BY_FILTER[filter], page, perPage: PER_PAGE },
  });

  const handleAction = async (type: 'decline' | 'link', requestId: string) => {
    if (type === 'decline') setDeclineRequestId(requestId);
    if (type === 'link') setLinkRequestId(requestId);
  };

  const declineRequestTarget = appRequests.nodes.find((request) => request.id === declineRequestId);
  const linkRequestTarget = appRequests.nodes.find((request) => request.id === linkRequestId);
  const linkCompanyName = linkRequestTarget?.vendorName ?? '';

  const handleLinkOpenChange = (open: boolean) => {
    if (!open) setLinkRequestId(null);
  };

  const handleLinkSubmit = (applicationId: string) => linkRequest(linkRequestId as string, applicationId);

  const handleCreateNewApplication = () => {
    navigate('/applications/new/app', { state: { appRequestId: linkRequestId, vendorName: linkCompanyName } });
  };

  return (
    <div className="space-y-4">
      {appRequests.nodesCount ? (
        <div className="space-y-3">
          {appRequests.nodes.map((request) => (
            <RequestCard key={request.id} request={request} direction={filter} onAction={handleAction} />
          ))}
        </div>
      ) : (
        <EmptyBlock
          icon={Inbox}
          title={`No ${filter} requests`}
          description="All your requests will be kept here for your records."
        />
      )}

      <Paginator
        currentPage={page}
        totalPages={appRequests.pagesCount}
        onPageChange={setPage}
        showPreviousNext
        hideOnSinglePage
      />

      <DeclineRequestDialog
        key={`decline-${declineRequestId}`}
        requestId={declineRequestId}
        companyName={declineRequestTarget?.vendorName ?? ''}
        onOpenChange={(open) => !open && setDeclineRequestId(null)}
        onSubmit={(reason) => declineRequest(declineRequestId as string, reason)}
      />

      <LinkApplicationDialog
        key={`link-${linkRequestId}`}
        open={linkRequestId !== null}
        onOpenChange={handleLinkOpenChange}
        title={`Link ${linkCompanyName} to an application`}
        description={`${linkCompanyName} will be able to see the application you choose and every protection in it.`}
        submitLabel="Link application"
        successMessage={`${linkCompanyName} added as a beneficiary`}
        onSubmit={handleLinkSubmit}
        onCreateNew={handleCreateNewApplication}
      />
    </div>
  );
};

export const VendorRequestsList = withSuspense(VendorRequestsListComponent, VendorRequestsListSkeleton);
